import type { PersonPlacementEvent } from "../../../api/threads/placementsApi";
import type { PlacedThread, ThreadSection, ThreadSections } from "./threadSections";

/**
 * What dragging a Thread in the sidebar does, as T3 Code's drop planner decides it (its
 * `Sidebar.logic`): onto Pinned it is pinned at that place, or the pins are reordered; onto
 * Active a pinned, settled or snoozed Thread comes back (unpinned, un-settled or woken) and
 * Active stays ordered by activity, as T3 Code's is with its Working shelf on; onto Settled it
 * is settled. A working Thread doesn't move, nor does a drop into Working or Snoozed. Pure: it
 * names the placement changes and the new pin order, the list makes them, the order last.
 * Module C gives the pins their places from that order (Sol, 27 Oct), so the browser makes no
 * order keys and a stale tab can't re-pin a Thread by moving its neighbours.
 */
export type DropSection = Extract<ThreadSection, "pinned" | "active" | "settled">;

export interface DropTarget {
  readonly section: DropSection;
  /** For Pinned: the pin the Thread goes before; `null` for the end. */
  readonly beforeThreadId: string | null;
}

export interface PlacementChange {
  readonly threadId: string;
  readonly event: PersonPlacementEvent;
}

export interface DropPlan {
  /** Made one after the other. */
  readonly changes: readonly PlacementChange[];
  /** The person's whole new pin order, sent once the changes are made; `null` keeps it. */
  readonly pinOrder: readonly string[] | null;
}

export const NO_DROP: DropPlan = { changes: [], pinOrder: null };

function sectionOf(sections: ThreadSections, threadId: string): { section: ThreadSection; thread: PlacedThread } | null {
  for (const section of Object.keys(sections) as ThreadSection[]) {
    const thread = sections[section].find((candidate) => candidate.summary.threadId === threadId);
    if (thread !== undefined) return { section, thread };
  }
  return null;
}

/**
 * `sections` holds all the person's Threads, so the new pin order keeps every pin in its place.
 */
export function planThreadDrop(sections: ThreadSections, threadId: string, target: DropTarget): DropPlan {
  const found = sectionOf(sections, threadId);
  if (found === null || found.section === "working") return NO_DROP;
  const { section, thread } = found;
  switch (target.section) {
    case "settled":
      return section === "settled" || !thread.summary.canSettleOrSnooze ? NO_DROP : { changes: [{ threadId, event: "settle" }], pinOrder: null };
    case "active":
      return { changes: backToActive(section, threadId, thread.placement?.pinnedAt != null), pinOrder: null };
    case "pinned": {
      // Dropped on itself: it stays where it is.
      if (target.beforeThreadId === threadId) return NO_DROP;
      const current = sections.pinned.map((pin) => pin.summary.threadId);
      const others = current.filter((id) => id !== threadId);
      const at = target.beforeThreadId === null ? -1 : others.indexOf(target.beforeThreadId);
      const order = at === -1 ? [...others, threadId] : [...others.slice(0, at), threadId, ...others.slice(at)];
      if (section === "pinned" && order.every((id, index) => id === current[index])) return NO_DROP;
      return { changes: section === "pinned" ? [] : [{ threadId, event: "pin" }], pinOrder: order };
    }
  }
}

/** Onto Active: a pinned, settled or snoozed Thread comes back; Active itself keeps its order. */
function backToActive(section: Exclude<ThreadSection, "working">, threadId: string, pinned: boolean): readonly PlacementChange[] {
  switch (section) {
    case "pinned":
      return [{ threadId, event: "unpin" }];
    case "settled":
      return [{ threadId, event: "unsettle" }];
    case "snoozed":
      return [{ threadId, event: "wake" }, ...(pinned ? [{ threadId, event: "unpin" as const }] : [])];
    case "active":
    case "shared":
      // Shared follows who hosts it, not a placement.
      return [];
  }
}

/**
 * A drop on a pin's row: on its upper half before that pin, on its lower half just after it
 * among all the pins, not before the next one shown, so a pin the filter hides keeps its place
 * and a pin dropped on itself stays put (Sol, 27 Oct).
 */
export function pinRowTarget(sections: ThreadSections, rowThreadId: string, upperHalf: boolean): DropTarget {
  if (upperHalf) return { section: "pinned", beforeThreadId: rowThreadId };
  const all = sections.pinned.map((pin) => pin.summary.threadId);
  return { section: "pinned", beforeThreadId: all[all.indexOf(rowThreadId) + 1] ?? null };
}

/**
 * Alt+↑ / Alt+↓ on a pin: the same as dragging it past the pin above or below that the list
 * shows (`shownPins`), for keyboard reordering.
 */
export function planPinMove(sections: ThreadSections, shownPins: readonly string[], threadId: string, direction: "up" | "down"): DropPlan {
  const index = shownPins.indexOf(threadId);
  if (index === -1) return NO_DROP;
  if (direction === "up") {
    return index === 0 ? NO_DROP : planThreadDrop(sections, threadId, { section: "pinned", beforeThreadId: shownPins[index - 1] ?? null });
  }
  const below = shownPins[index + 1];
  return below === undefined ? NO_DROP : planThreadDrop(sections, threadId, pinRowTarget(sections, below, false));
}
